import { memo, useState, useRef, useEffect } from 'react';
import 'prop-types';
import { useDispatch } from 'react-redux';
import { Toast, Button } from 'reaxl';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import { sendImpressions, sendClick } from 'reaxl-analytics';
import { myWalletNonDealNotificationsImpression, seeDetailsGlobalNavDealImpression, seeDetailsGlobalNavDealClick, myWalletNonDealNotificationsClick } from 'reaxl-analytics-handlers';
import { WALLET_RESET_DEAL_NOTIFICATION_REQUEST, WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST } from 'wallet-manager';
import { eventBus } from '@atc/cai-event-bus';
import { BrowserStorage } from '@atc/browser-storage';
import { jsx, jsxs } from 'react/jsx-runtime';
import getNotificationData from './helpers/getNotificationData.js';
import getLocationMessage from '../utilities/notifications/getLocationMessage.js';
const walletNotifCache = /* #__PURE__*/new BrowserStorage('WALLET_NOTIF_POPOVER', {
  persist: true
});
const WalletNotification = /*#__PURE__*/memo(_ref => {
  let {
    dealDetails = {},
    dealsDuck,
    dealNotifications,
    onClick,
    onCloseClick = () => {},
    hasNewTradeIn,
    paymentsDuck,
    position = 'top'
  } = _ref;
  const dispatch = useDispatch();
  const [showWalletAlert, setShowWalletAlert] = useState(true);
  const sentImpression = useRef(false);
  const notificationData = getNotificationData(hasNewTradeIn, dealNotifications, dealDetails);
  useEffect(() => {
    const dismissalCache = walletNotifCache?.getItem();

    // Check the global 'show' flag used by other components
    if (dismissalCache?.show === false) {
      setShowWalletAlert(false);
    }
  }, [notificationData, dealNotifications]);
  useEffect(() => {
    const isNonDeal = notificationData?.dealReminders?.length > 0;
    const showNotification = showWalletAlert;
    if (sentImpression.current || !showNotification || _isEmpty(notificationData)) {
      return;
    }
    sentImpression.current = true;
    if (isNonDeal) {
      sendImpressions({
        name: myWalletNonDealNotificationsImpression,
        data: {
          textContent: notificationData.label
        }
      });
    } else {
      sendImpressions({
        name: seeDetailsGlobalNavDealImpression,
        data: {
          location: getLocationMessage(hasNewTradeIn, dealNotifications)
        }
      });
    }
  }, [notificationData, showWalletAlert, hasNewTradeIn, dealNotifications]);
  const updateDealReminders = () => {
    notificationData?.dealReminders?.forEach(_ref2 => {
      let {
        dealId,
        updateKey
      } = _ref2;
      eventBus.emit(WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST, {
        dealId,
        body: {
          [updateKey]: true
        }
      });
      const action = dealsDuck?.creators?.updateDealReminders({
        dealId,
        [updateKey]: true
      });
      if (action) {
        try {
          dispatch(action);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error updating deal reminders', error);
        }
      }
    });
  };
  const handleNonDealClick = e => {
    const {
      deal
    } = notificationData;
    const nonDealData = {
      textContent: e.target.textContent,
      vehicle: dealDetails?.[deal.id]?.vehicle
    };
    updateDealReminders();
    sendClick(myWalletNonDealNotificationsClick, e, nonDealData);
  };
  const handleNotificationLinkClick = e => {
    if (notificationData?.dealReminders?.length > 0) {
      handleNonDealClick(e);
    } else {
      sendClick(seeDetailsGlobalNavDealClick, e, {
        location: getLocationMessage(hasNewTradeIn, dealNotifications)
      });
    }
    onClick(e);
    onCloseClick();
    setShowWalletAlert(false);
  };
  const handleNotificationCloseClick = () => {
    const isDealRelated = notificationData?.panel === 'activeDeals' || notificationData?.dealReminders?.length > 0;
    if (isDealRelated) {
      const dealIds = notificationData?.dealReminders?.map(_ref3 => {
        let {
          dealId
        } = _ref3;
        return dealId;
      }) || [];
      eventBus.emit(WALLET_RESET_DEAL_NOTIFICATION_REQUEST, {
        dealIds
      });
      dispatch(paymentsDuck?.creators?.resetDealNotifications());
      updateDealReminders();
    }
    onCloseClick();
    setShowWalletAlert(false);
  };
  if (_isEmpty(notificationData) || !showWalletAlert) {
    return null;
  }
  return /*#__PURE__*/jsx(Toast, {
    show: true,
    uiContext: "info",
    speed: "slow",
    position: position,
    heading: "My Wallet Alert!",
    onDismiss: handleNotificationCloseClick,
    body: /*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsx("div", {
        children: notificationData.message
      }), /*#__PURE__*/jsx(Button, {
        bsStyle: "text-decorated-inverse",
        className: "padding-0 border-0",
        "data-panel": notificationData.panel,
        onClick: handleNotificationLinkClick,
        children: notificationData.label || 'See Details'
      })]
    })
  });
}, (prevProps, nextProps) => _isEqual(prevProps, nextProps));
export { WalletNotification as default };
